import {
	SceneTransform,
	Settings,
	LeftPanelTab,
	Scaling,
	GoldTooltipState,
	SilverTooltipState,
} from 'redux/mode/reducers';
import { Command } from 'redux/mode/commands';
import { Quality } from 'dragonmouse-shared/interfaces';
import { TokenCategory, PrimitiveCategory, ComponentCategory } from 'designSystem/types';

// Data that should persist across browser sessions and be shared across tabs
export interface LocalData {
	transforms?: { [sceneId: string]: SceneTransform };
	leftPanelWidths?: { [tab in LeftPanelTab]: number };
	rightPanelWidth?: number;
	designSystemChatPanelWidth?: number;
	settings?: Settings;
	prototypeScaling?: Scaling;
	chatQuality?: Quality;
	sidebarVisible?: boolean;
	sidebarVisibilityInitialized?: boolean;
	goldTooltipState?: GoldTooltipState;
	silverTooltipState?: SilverTooltipState;
	designSystemFilters?: {
		tokens: {
			selectedCategories: TokenCategory[];
			searchQuery: string;
		};
		styles: {
			selectedCategories: PrimitiveCategory[];
			searchQuery: string;
		};
		components: {
			selectedCategories: ComponentCategory[];
			searchQuery: string;
		};
		stories: {
			searchQuery: string;
		};
	};
}

// Data that should be per-tab and not persist across browser sessions
export interface SessionData {
	activeSceneChatId?: string | null;
	activeLeftPanelTab?: LeftPanelTab;
	commandStack?: Command[];
	undoneCommands?: Command[];
	centeredFrameId?: string | null;
	trackedUserIds?: string[];
	panelsVisible?: boolean;
}

// Core idea here is that storing stuff long-term can be handled here, since there
// is some interplay between parts of this. Could switch more stuff to this pattern,
// which allows us to do a simple get / set when using it and change the way it's
// stored without changing the rest of the code.
class Storage {
	conduitInitialized: boolean = false;
	sceneFramesRetrieved: boolean = false;

	get local(): LocalData {
		const localData: LocalData = {};

		const jsonText = localStorage.getItem('mode');
		if (jsonText) {
			const saved = JSON.parse(jsonText);
			if (typeof saved === 'object') {
				// TODO: consider using lodash's merge to make this more compact - https://lodash.com/docs/4.17.15#merge
				// since merge doesn't seem to deal with the Number.isFinite cases above, I'm not adding it just yet...
				if (saved.transforms !== undefined) {
					localData.transforms = { ...saved.transforms };
				}
				if (saved.leftPanelWidths !== undefined) {
					localData.leftPanelWidths = { ...saved.leftPanelWidths };
				}
				if (saved.rightPanelWidth !== undefined) {
					localData.rightPanelWidth = saved.rightPanelWidth;
				}
				if (saved.designSystemChatPanelWidth !== undefined) {
					localData.designSystemChatPanelWidth = saved.designSystemChatPanelWidth;
				}
				if (saved.settings !== undefined) {
					localData.settings = { ...saved.settings };
				}
				if (saved.prototypeScaling !== undefined) {
					localData.prototypeScaling = saved.prototypeScaling;
				}
				if (saved.chatQuality !== undefined) {
					localData.chatQuality = saved.chatQuality;
				}
				if (saved.sidebarVisible !== undefined) {
					localData.sidebarVisible = saved.sidebarVisible;
				}
				if (saved.sidebarVisibilityInitialized !== undefined) {
					localData.sidebarVisibilityInitialized = saved.sidebarVisibilityInitialized;
				}
				if (saved.goldTooltipState !== undefined) {
					localData.goldTooltipState = saved.goldTooltipState;
				}
				if (saved.silverTooltipState !== undefined) {
					localData.silverTooltipState = saved.silverTooltipState;
				}
				if (saved.designSystemFilters !== undefined) {
					localData.designSystemFilters = { ...saved.designSystemFilters };
				}
			}
		}
		return localData;
	}

	set local(data: LocalData) {
		localStorage.setItem('mode', JSON.stringify(data));
	}

	get session(): SessionData {
		const sessionData: SessionData = {};

		const jsonText = sessionStorage.getItem('mode');
		if (jsonText) {
			const saved = JSON.parse(jsonText);
			if (typeof saved === 'object') {
				if (saved.activeSceneChatId !== undefined) {
					sessionData.activeSceneChatId = saved.activeSceneChatId;
				}
				if (saved.activeLeftPanelTab !== undefined) {
					sessionData.activeLeftPanelTab = saved.activeLeftPanelTab;
				}
				if (saved.commandStack !== undefined) {
					sessionData.commandStack = [...saved.commandStack];
				}
				if (saved.undoneCommands !== undefined) {
					sessionData.undoneCommands = [...saved.undoneCommands];
				}
				if (saved.centeredFrameId !== undefined) {
					sessionData.centeredFrameId = saved.centeredFrameId;
				}
				if (saved.trackedUserIds !== undefined) {
					sessionData.trackedUserIds = [...saved.trackedUserIds];
				}
				if (saved.panelsVisible !== undefined) {
					sessionData.panelsVisible = saved.panelsVisible;
				}
			}
		}
		return sessionData;
	}

	set session(data: SessionData) {
		sessionStorage.setItem('mode', JSON.stringify(data));
	}
}

export default new Storage();
